import React, { useState, useEffect, useMemo } from "react"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./target-tracker.scss"

// ChartJS imports
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title as ChartTitle, Tooltip, Legend } from "chart.js"
import annotationPlugin from "chartjs-plugin-annotation"
import { Line } from "react-chartjs-2"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Button from "../../components/ui/button/button"
import { TrendingDown, Target, AlertTriangle, CheckCircle, Loader2 } from "lucide-react"

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend, annotationPlugin)

// Target constants
const MARGIN_GROWTH_TARGET = 17 // 17% YoY growth
const CONVERSION_RATE_TARGET = 17 // 17% average conversion rate

// Calculate default date range (Nov 1st to April 30th)
const getDefaultDateRange = () => {
	const now = moment()
	const currentYear = now.year()
	const currentMonth = now.month() // 0-indexed

	// If we're between November and December, use current year Nov to next year April
	// If we're between January and October, use previous year Nov to current year April
	let startYear, endYear
	if (currentMonth >= 10) {
		// November (10) or December (11)
		startYear = currentYear
		endYear = currentYear + 1
	} else {
		startYear = currentYear - 1
		endYear = currentYear
	}

	return {
		startDate: moment(`${startYear}-11-01`).startOf("day").toDate(),
		endDate: moment(`${endYear}-04-30`).endOf("day").toDate(),
	}
}

// Returns the HTML markup for the Target Tracker page
export default function TargetTracker() {
	const defaultRange = getDefaultDateRange()
	const [startDate, setStartDate] = useState(defaultRange.startDate)
	const [endDate, setEndDate] = useState(defaultRange.endDate)
	const [loading, setLoading] = useState(false)
	const [dataLoaded, setDataLoaded] = useState(false)

	// Track the last fetched dates to show update button when changed
	const [lastFetchedStart, setLastFetchedStart] = useState(null)
	const [lastFetchedEnd, setLastFetchedEnd] = useState(null)

	// Margin data
	const [marginData, setMarginData] = useState({
		currentPeriod: [],
		lastYearPeriod: [],
		dailyGrowthRates: [],
		labels: [],
		currentTotal: 0,
		lastYearAtSamePoint: 0,
		overallGrowth: 0,
	})

	// Conversion data
	const [conversionData, setConversionData] = useState({
		dailyRates: [],
		runningAverage: [],
		labels: [],
		totalEnquiries: 0,
		totalBookings: 0,
		overallRate: 0,
	})

	// Check if dates have changed from last fetch
	const datesChanged = useMemo(() => {
		if (!lastFetchedStart || !lastFetchedEnd) return false
		return moment(startDate).format("YYYY-MM-DD") !== moment(lastFetchedStart).format("YYYY-MM-DD") || moment(endDate).format("YYYY-MM-DD") !== moment(lastFetchedEnd).format("YYYY-MM-DD")
	}, [startDate, endDate, lastFetchedStart, lastFetchedEnd])

	// Fetch data on initial mount
	useEffect(() => {
		if (startDate && endDate) {
			fetchAllData()
		}
	}, [])

	// Fetch all data
	const fetchAllData = async () => {
		setLoading(true)
		setDataLoaded(false)

		try {
			await Promise.all([fetchMarginData(), fetchConversionData()])
			// Save the fetched dates
			setLastFetchedStart(startDate)
			setLastFetchedEnd(endDate)
		} catch (err) {
			console.error("Error fetching data:", err)
		} finally {
			setLoading(false)
			setDataLoaded(true)
		}
	}

	// Fetch margin data for YoY comparison
	const fetchMarginData = async () => {
		const startMoment = moment(startDate)
		const endMoment = moment(endDate)

		// Current period timestamps
		const currentStartTs = firebase.firestore.Timestamp.fromMillis(startMoment.valueOf())
		const currentEndTs = firebase.firestore.Timestamp.fromMillis(endMoment.valueOf())

		// Last year period (same dates, one year earlier)
		const lastYearStart = startMoment.clone().subtract(1, "year")
		const lastYearEnd = endMoment.clone().subtract(1, "year")
		const lastYearStartTs = firebase.firestore.Timestamp.fromMillis(lastYearStart.valueOf())
		const lastYearEndTs = firebase.firestore.Timestamp.fromMillis(lastYearEnd.valueOf())

		// Fetch bookings for both periods
		const [currentBookings, lastYearBookings] = await Promise.all([
			db.collection("bookings").where("created", ">=", currentStartTs).where("created", "<=", currentEndTs).get(),
			db.collection("bookings").where("created", ">=", lastYearStartTs).where("created", "<=", lastYearEndTs).get(),
		])

		// Process current period bookings by date
		const currentByDate = {}
		let currentTotal = 0
		currentBookings.docs.forEach((doc) => {
			const data = doc.data()
			const margin = Math.round(data.margins?.profit) || 0

			const dateKey = moment(data.created.seconds * 1000).format("YYYY-MM-DD")
			currentByDate[dateKey] = (currentByDate[dateKey] || 0) + margin
			currentTotal += margin
		})

		// Process last year bookings by date
		const lastYearByDate = {}
		let lastYearTotal = 0
		lastYearBookings.docs.forEach((doc) => {
			const data = doc.data()
			const margin = Math.round(data.margins?.profit) || 0

			const dateKey = moment(data.created.seconds * 1000).format("YYYY-MM-DD")
			lastYearByDate[dateKey] = (lastYearByDate[dateKey] || 0) + margin
			lastYearTotal += margin
		})

		// Generate all dates in range
		const allDates = []
		const currentDate = startMoment.clone()
		const today = moment()

		while (currentDate.isSameOrBefore(endMoment)) {
			// Only include dates up to today if end date is in the future
			if (currentDate.isAfter(today)) break
			allDates.push(currentDate.format("YYYY-MM-DD"))
			currentDate.add(1, "day")
		}

		// Calculate cumulative margins and growth rates
		let currentCumulative = 0
		let lastYearCumulative = 0
		const currentPeriod = []
		const lastYearPeriod = []
		const dailyGrowthRates = []
		const labels = []

		allDates.forEach((date) => {
			const lastYearDate = moment(date).subtract(1, "year").format("YYYY-MM-DD")

			currentCumulative += currentByDate[date] || 0
			lastYearCumulative += lastYearByDate[lastYearDate] || 0

			currentPeriod.push(currentCumulative)
			lastYearPeriod.push(lastYearCumulative)

			// Calculate YoY growth rate
			const growthRate = lastYearCumulative > 0 ? ((currentCumulative - lastYearCumulative) / lastYearCumulative) * 100 : 0

			dailyGrowthRates.push(growthRate)
			labels.push(moment(date).format("DD MMM"))
		})

		// Calculate overall growth - compare current cumulative to last year at the SAME POINT in time
		// (not last year's full period total)
		const overallGrowth = lastYearCumulative > 0 ? ((currentCumulative - lastYearCumulative) / lastYearCumulative) * 100 : 0

		setMarginData({
			currentPeriod,
			lastYearPeriod,
			dailyGrowthRates,
			labels,
			currentTotal: currentCumulative,
			lastYearAtSamePoint: lastYearCumulative,
			overallGrowth,
		})
	}

	// Fetch conversion data
	const fetchConversionData = async () => {
		const startMoment = moment(startDate)
		const endMoment = moment(endDate)
		const startTs = firebase.firestore.Timestamp.fromMillis(startMoment.valueOf())
		const endTs = firebase.firestore.Timestamp.fromMillis(endMoment.valueOf())

		// Filter test accounts
		const testAccounts = ["apH2Vpa4qns0YTnelDMX", "UPyzRTUdKGlJgiCCxseP", "RxnT7D5WGjPddb9dSCp4"]

		// Fetch all enquiries (open and removed) and bookings
		const [openEnquiries, removedEnquiries, enquiryBookingsSnapshot, bookingsCreatedInRangeSnapshot] = await Promise.all([
			db.collection("enquiries").where("created", ">=", startTs).where("created", "<=", endTs).get(),
			db.collection("enquiries_removed").where("enquiry.created", ">=", startTs).where("enquiry.created", "<=", endTs).get(),
			// Enquiry-based bookings: attribute to the enquiry date
			db.collection("bookings").where("enquiry.created", ">=", startTs).where("enquiry.created", "<=", endTs).get(),
			// Direct bookings: no enquiry.created, attribute to booking.created
			db.collection("bookings").where("created", ">=", startTs).where("created", "<=", endTs).get(),
		])

		// Process open enquiries (exclude archived from stats)
		const openEnquiriesList = openEnquiries.docs
			.filter((doc) => doc.data().archived_from_stats !== true)
			.map((doc) => ({
				created: moment(doc.data().created.seconds * 1000),
				client: doc.data().client,
				location: doc.data().location,
			}))

		// Process removed enquiries (exclude archived from stats)
		const removedEnquiriesList = removedEnquiries.docs
			.filter((doc) => doc.data().archived_from_stats !== true)
			.map((doc) => ({
				created: moment(doc.data().enquiry.created.seconds * 1000),
				client: doc.data().enquiry?.client,
				location: doc.data().enquiry?.location,
			}))

		// Combine enquiries
		const allEnquiries = [...openEnquiriesList, ...removedEnquiriesList]

		const filteredEnquiries = allEnquiries.filter((enq) => !testAccounts.includes(enq.client))

		// Process enquiry-based bookings (for matching enquiries -> booking conversions)
		const enquiryBookingsList = enquiryBookingsSnapshot.docs
			.map((doc) => {
				const data = doc.data()
				const enquiryCreatedSeconds = data.enquiry?.created?.seconds
				if (!enquiryCreatedSeconds) return null

				return {
					created: moment(enquiryCreatedSeconds * 1000),
					client: data.client,
					location: data.enquiry?.location,
				}
			})
			.filter(Boolean)
			.filter((b) => b.client && !testAccounts.includes(b.client))

		// Process direct bookings (count toward bookings only; bucket by booking.created)
		const directBookingsList = bookingsCreatedInRangeSnapshot.docs
			.map((doc) => {
				const data = doc.data()
				if (data.booking_type !== "DIRECT") return null

				const createdSeconds = data.created?.seconds
				if (!createdSeconds) return null

				return {
					created: moment(createdSeconds * 1000),
					client: data.client,
				}
			})
			.filter(Boolean)
			.filter((b) => b.client && !testAccounts.includes(b.client))

		// Mark enquiries that converted to bookings
		filteredEnquiries.forEach((enquiry) => {
			const booking = enquiryBookingsList.find((b) => b.client === enquiry.client && b.location === enquiry.location && b.created.format("YYYY-MM-DD") === enquiry.created.format("YYYY-MM-DD"))
			if (booking) enquiry.booked = true
		})

		// Get booked enquiries
		const bookedEnquiries = filteredEnquiries.filter((e) => e.booked)

		// Get non-booked enquiries that don't have any booking for that client
		const nonBookedEnquiries = filteredEnquiries.filter((e) => !e.booked)
		const uniqueNonBooked = nonBookedEnquiries.filter((enquiry) => {
			const hasAnyBooking = enquiryBookingsList.some((b) => b.client === enquiry.client) || directBookingsList.some((b) => b.client === enquiry.client)
			return !hasAnyBooking
		})

		// De-duplicate by client
		const deduplicatedNonBooked = uniqueNonBooked.filter((enquiry, index, self) => self.findIndex((e) => e.client === enquiry.client) === index)

		// Final enquiries list
		const finalEnquiries = [...deduplicatedNonBooked, ...bookedEnquiries]

		// Group enquiries and bookings by date
		const enquiriesByDate = {}
		const bookingsByDate = {}

		finalEnquiries.forEach((enq) => {
			const dateKey = enq.created.format("YYYY-MM-DD")
			enquiriesByDate[dateKey] = (enquiriesByDate[dateKey] || 0) + 1
		})

		bookedEnquiries.forEach((enq) => {
			const dateKey = enq.created.format("YYYY-MM-DD")
			bookingsByDate[dateKey] = (bookingsByDate[dateKey] || 0) + 1
		})

		// Add direct bookings into booking buckets (numerator only), bucketed by booking.created
		directBookingsList.forEach((booking) => {
			const dateKey = booking.created.format("YYYY-MM-DD")
			bookingsByDate[dateKey] = (bookingsByDate[dateKey] || 0) + 1
		})

		// Generate all dates in range
		const allDates = []
		const currentDate = startMoment.clone()
		const today = moment()

		while (currentDate.isSameOrBefore(endMoment)) {
			if (currentDate.isAfter(today)) break
			allDates.push(currentDate.format("YYYY-MM-DD"))
			currentDate.add(1, "day")
		}

		// Calculate running conversion rates
		let cumulativeEnquiries = 0
		let cumulativeBookings = 0
		const dailyRates = []
		const runningAverage = []
		const labels = []

		allDates.forEach((date) => {
			cumulativeEnquiries += enquiriesByDate[date] || 0
			cumulativeBookings += bookingsByDate[date] || 0

			const dailyEnq = enquiriesByDate[date] || 0
			const dailyBook = bookingsByDate[date] || 0
			const dailyRate = dailyEnq > 0 ? (dailyBook / dailyEnq) * 100 : null

			dailyRates.push(dailyRate)

			const avgRate = cumulativeEnquiries > 0 ? (cumulativeBookings / cumulativeEnquiries) * 100 : 0
			runningAverage.push(avgRate)

			labels.push(moment(date).format("DD MMM"))
		})

		// Calculate overall rate
		const totalBookings = bookedEnquiries.length + directBookingsList.length
		const overallRate = finalEnquiries.length > 0 ? (totalBookings / finalEnquiries.length) * 100 : 0

		setConversionData({
			dailyRates,
			runningAverage,
			labels,
			totalEnquiries: finalEnquiries.length,
			totalBookings,
			overallRate,
		})
	}

	// Calculate prediction for margin growth
	const marginPrediction = useMemo(() => {
		if (!dataLoaded || marginData.dailyGrowthRates.length === 0) {
			return { status: "loading", message: "Calculating...", projected: 0 }
		}

		const currentGrowth = marginData.overallGrowth
		const daysElapsed = marginData.dailyGrowthRates.length
		const totalDays = moment(endDate).diff(moment(startDate), "days") + 1
		const daysRemaining = totalDays - daysElapsed

		// Simple linear projection based on current trend
		if (daysElapsed < 7) {
			return { status: "insufficient", message: "Need more data", projected: currentGrowth }
		}

		// Calculate trend over last 7 days
		const recentGrowth = marginData.dailyGrowthRates.slice(-7)
		const growthTrend = (recentGrowth[recentGrowth.length - 1] - recentGrowth[0]) / 7

		// Project to end of period
		const projectedGrowth = currentGrowth + growthTrend * daysRemaining

		if (currentGrowth >= MARGIN_GROWTH_TARGET) {
			return { status: "on_track", message: "On Track", projected: currentGrowth }
		} else if (projectedGrowth >= MARGIN_GROWTH_TARGET) {
			return { status: "on_track", message: "On Track", projected: projectedGrowth }
		} else if (projectedGrowth >= MARGIN_GROWTH_TARGET * 0.8) {
			return { status: "at_risk", message: "At Risk", projected: projectedGrowth }
		} else {
			return { status: "unlikely", message: "Unlikely", projected: projectedGrowth }
		}
	}, [marginData, dataLoaded, startDate, endDate])

	// Calculate prediction for conversion rate
	const conversionPrediction = useMemo(() => {
		if (!dataLoaded || conversionData.runningAverage.length === 0) {
			return { status: "loading", message: "Calculating...", projected: 0 }
		}

		const currentRate = conversionData.overallRate

		if (conversionData.runningAverage.length < 7) {
			return { status: "insufficient", message: "Need more data", projected: currentRate }
		}

		// Use the running average as the projection (it stabilizes over time)
		const projectedRate = currentRate

		if (currentRate >= CONVERSION_RATE_TARGET) {
			return { status: "on_track", message: "On Track", projected: currentRate }
		} else if (projectedRate >= CONVERSION_RATE_TARGET * 0.95) {
			return { status: "on_track", message: "On Track", projected: projectedRate }
		} else if (projectedRate >= CONVERSION_RATE_TARGET * 0.8) {
			return { status: "at_risk", message: "At Risk", projected: projectedRate }
		} else {
			return { status: "unlikely", message: "Unlikely", projected: projectedRate }
		}
	}, [conversionData, dataLoaded])

	// Margin growth chart data - show both cumulative margins and growth rate
	const marginChartData = {
		labels: marginData.labels,
		datasets: [
			{
				label: "YoY Margin Growth %",
				data: marginData.dailyGrowthRates,
				borderColor: "#e41f13",
				backgroundColor: "rgba(228, 31, 19, 0.1)",
				borderWidth: 2,
				pointRadius: 1,
				pointHoverRadius: 4,
				fill: true,
				tension: 0.2,
				yAxisID: "y",
			},
			{
				label: "Target (17%)",
				data: marginData.labels.map(() => MARGIN_GROWTH_TARGET),
				borderColor: "rgba(34, 197, 94, 0.8)",
				borderWidth: 2,
				borderDash: [10, 5],
				pointRadius: 0,
				fill: false,
				yAxisID: "y",
			},
		],
	}

	// Separate chart for cumulative margin comparison
	const marginComparisonChartData = {
		labels: marginData.labels,
		datasets: [
			{
				label: "This Year (Cumulative £)",
				data: marginData.currentPeriod,
				borderColor: "#e41f13",
				backgroundColor: "rgba(228, 31, 19, 0.1)",
				borderWidth: 2,
				pointRadius: 1,
				pointHoverRadius: 4,
				fill: false,
				tension: 0.2,
			},
			{
				label: "Last Year (Cumulative £)",
				data: marginData.lastYearPeriod,
				borderColor: "#6b7280",
				backgroundColor: "rgba(107, 114, 128, 0.1)",
				borderWidth: 2,
				borderDash: [5, 5],
				pointRadius: 1,
				pointHoverRadius: 4,
				fill: false,
				tension: 0.2,
			},
		],
	}

	// Conversion rate chart data
	const conversionChartData = {
		labels: conversionData.labels,
		datasets: [
			{
				label: "Running Average Conversion %",
				data: conversionData.runningAverage,
				borderColor: "#3b82f6",
				backgroundColor: "rgba(59, 130, 246, 0.1)",
				borderWidth: 2,
				pointRadius: 1,
				pointHoverRadius: 4,
				fill: true,
				tension: 0.2,
			},
			{
				label: "Target (17%)",
				data: conversionData.labels.map(() => CONVERSION_RATE_TARGET),
				borderColor: "rgba(34, 197, 94, 0.8)",
				borderWidth: 2,
				borderDash: [10, 5],
				pointRadius: 0,
				fill: false,
			},
			{
				label: "Stretch Target (19%)",
				data: conversionData.labels.map(() => 19),
				borderColor: "rgba(147, 51, 234, 0.8)",
				borderWidth: 2,
				borderDash: [5, 3],
				pointRadius: 0,
				fill: false,
			},
		],
	}

	// Chart options for percentage charts
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "top",
				align: "end",
			},
			tooltip: {
				mode: "index",
				intersect: false,
				callbacks: {
					label: (context) => {
						const value = context.parsed.y
						if (value === null || value === undefined) return null
						return `${context.dataset.label}: ${value.toFixed(2)}%`
					},
				},
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					maxRotation: 45,
					minRotation: 45,
					autoSkip: true,
					maxTicksLimit: 20,
				},
			},
			y: {
				beginAtZero: false,
				grid: {
					color: "rgba(0, 0, 0, 0.05)",
				},
				ticks: {
					callback: (value) => `${value.toFixed(0)}%`,
				},
			},
		},
		interaction: {
			mode: "index",
			intersect: false,
		},
	}

	// Chart options for currency comparison chart
	const currencyChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "top",
				align: "end",
			},
			tooltip: {
				mode: "index",
				intersect: false,
				callbacks: {
					label: (context) => {
						const value = context.parsed.y
						if (value === null || value === undefined) return null
						return `${context.dataset.label}: £${value.toLocaleString()}`
					},
				},
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					maxRotation: 45,
					minRotation: 45,
					autoSkip: true,
					maxTicksLimit: 20,
				},
			},
			y: {
				beginAtZero: true,
				grid: {
					color: "rgba(0, 0, 0, 0.05)",
				},
				ticks: {
					callback: (value) => `£${(value / 1000).toFixed(0)}k`,
				},
			},
		},
		interaction: {
			mode: "index",
			intersect: false,
		},
	}

	// Get prediction icon
	const getPredictionIcon = (status) => {
		switch (status) {
			case "on_track":
				return <CheckCircle className="prediction-icon on-track" />
			case "at_risk":
				return <AlertTriangle className="prediction-icon at-risk" />
			case "unlikely":
				return <TrendingDown className="prediction-icon unlikely" />
			default:
				return <Target className="prediction-icon loading" />
		}
	}

	// Handle date changes
	const handleStartDateChange = (milliseconds) => {
		setStartDate(moment(milliseconds).toDate())
	}

	const handleEndDateChange = (milliseconds) => {
		setEndDate(moment(milliseconds).toDate())
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Target Tracker</h1>
			</Title>

			<div className="target-tracker-controls">
				<div className="date-picker-group">
					<div className="date-picker-wrapper">
						<label>From:</label>
						<Datepicker
							placeholder="Start Date"
							value={moment(startDate).valueOf()}
							onSelect={handleStartDateChange}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>To:</label>
						<Datepicker
							placeholder="End Date"
							value={moment(endDate).valueOf()}
							onSelect={handleEndDateChange}
						/>
					</div>
					{datesChanged && (
						<Button
							label="Update"
							small={true}
							onClick={fetchAllData}
							loading={loading}
							loadingText="Updating..."
						/>
					)}
				</div>

				<div className="target-period-info">
					<span>
						Selected: {moment(startDate).format("MMM Do")} - {moment(endDate).format("MMM Do YYYY")}
					</span>
				</div>
			</div>

			{loading ? (
				<div className="loading-container">
					<div className="loading-content">
						<Loader2
							className="loading-spinner"
							size={18}
						/>
						<span>Loading target data</span>
					</div>
				</div>
			) : (
				<div className="charts-container">
					{/* Conversion Rate Section */}
					<div className="chart-section">
						<div className="prediction-card-container">
							<div className={`prediction-card ${conversionPrediction.status}`}>
								{getPredictionIcon(conversionPrediction.status)}
								<div className="prediction-content">
									<span className="prediction-label">Conversion Rate Target</span>
									<span className="prediction-status">{conversionPrediction.message}</span>
									<div className="prediction-details">
										<span className="current-value">
											Current: <strong>{conversionData.overallRate.toFixed(2)}%</strong>
										</span>
										<span className="target-value">Target: {CONVERSION_RATE_TARGET}%</span>
									</div>
									<div className="prediction-stats">
										<span>
											{conversionData.totalBookings} bookings / {conversionData.totalEnquiries} enquiries
										</span>
										{conversionData.totalEnquiries > 0 &&
											(() => {
												const bookingsNeededFor17 = Math.ceil(conversionData.totalEnquiries * (CONVERSION_RATE_TARGET / 100)) - conversionData.totalBookings
												const bookingsNeededFor19 = Math.ceil(conversionData.totalEnquiries * 0.19) - conversionData.totalBookings
												return (
													<span className="bookings-needed">
														{bookingsNeededFor17 > 0 ? `Need ${bookingsNeededFor17} more booking${bookingsNeededFor17 !== 1 ? "s" : ""} for 17%` : "17% target reached!"}
														{bookingsNeededFor17 <= 0 && bookingsNeededFor19 > 0 && ` • Need ${bookingsNeededFor19} more for 19%`}
														{bookingsNeededFor19 <= 0 && " • 19% target reached!"}
													</span>
												)
											})()}
									</div>
								</div>
							</div>
						</div>

						<div className="chart-wrapper">
							<h3>Conversion Rate Progress</h3>
							<div className="chart-container">
								<Line
									data={conversionChartData}
									options={chartOptions}
								/>
							</div>
						</div>
					</div>

					{/* Margin Growth Section */}
					<div className="chart-section">
						<div className="prediction-card-container">
							<div className={`prediction-card ${marginPrediction.status}`}>
								{getPredictionIcon(marginPrediction.status)}
								<div className="prediction-content">
									<span className="prediction-label">YoY Margin Growth Target</span>
									<span className="prediction-status">{marginPrediction.message}</span>
									<div className="prediction-details">
										<span className="current-value">
											Current Growth:{" "}
											<strong>
												{marginData.overallGrowth >= 0 ? "+" : ""}
												{marginData.overallGrowth.toFixed(2)}%
											</strong>
										</span>
										<span className="target-value">Target: +{MARGIN_GROWTH_TARGET}%</span>
									</div>
									<div className="prediction-stats">
										<span>
											This period: £{marginData.currentTotal.toLocaleString()} vs Same point last year: £{marginData.lastYearAtSamePoint.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
						</div>

						<div className="chart-wrapper">
							<h3>Cumulative Margin: This Year vs Last Year</h3>
							<div className="chart-container">
								<Line
									data={marginComparisonChartData}
									options={currencyChartOptions}
								/>
							</div>
						</div>

						<div
							className="chart-wrapper"
							style={{ marginTop: "24px" }}>
							<h3>YoY Margin Growth Rate (%)</h3>
							<div className="chart-container">
								<Line
									data={marginChartData}
									options={chartOptions}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
		</Tile>
	)
}
